import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, ChevronDown, Volume2, VolumeX, Loader2, X } from "lucide-react";
import { useAudio } from "../context/AudioContext";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const Equalizer = ({ playing = false }) => (
  <span className={`audio-eq ${playing ? "is-playing" : ""}`} aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className="audio-eq__bar" style={{ animationDelay: `${i * 0.12}s` }} />
    ))}
  </span>
);

export const GlobalAudioPlayer = () => {
  const {
    current, currentId, isPlaying, loading, progress, duration, volume,
    toggle, seek, setVolume, stop,
  } = useAudio();

  const [minimized, setMinimized] = useState(false);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    setMinimized(false);
  }, [currentId]);

  useEffect(() => {
    if (!isPlaying && !loading) setMinimized(true);
  }, [isPlaying, loading]);

  if (!current) return null;
  const isPaused = !isPlaying && !loading;

  const handleChipClick = () => {
    setMinimized(false);
    if (isPaused) toggle();
  };

  const onSeek = (e) => {
    const max = Math.max(duration, 1);
    seek(parseFloat(e.target.value) / max);
  };

  return (
    <AnimatePresence>
      {minimized ? (
        <motion.button
          key="audio-chip"
          data-testid="audio-chip"
          type="button"
          onClick={handleChipClick}
          aria-label={isPlaying ? `Expand audio player - ${current.title}` : "Expand audio player"}
          initial={{ opacity: 0, y: 24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.95 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-5 right-5 z-[96] inline-flex items-center gap-3 max-w-[min(20rem,calc(100vw-2.5rem))] py-2 pr-2 pl-4 rounded-full bg-[rgba(14,11,8,0.92)] backdrop-blur border border-[#E2B87E]/35 shadow-[0_10px_40px_rgba(226,184,126,0.25)] text-[#F3EEE3] cursor-pointer hover:border-[#E2B87E]/60 transition-colors"
        >
          <Equalizer playing={isPlaying} />
          <span className="min-w-0 text-left text-[17px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
            {current.title}
            <span className="block text-base font-normal text-[#F3EEE3]/75">
              {isPlaying ? "Now playing" : "Tap to expand"}
            </span>
          </span>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#E8702A] to-[#D43D7E] text-[#0E0B08] shrink-0">
            <Play size={12} fill="currentColor" />
          </span>
        </motion.button>
      ) : (
        <motion.div
          key="audio-dock"
          data-testid="audio-player-bar"
          role="region"
          aria-label="Audio player"
          initial={{ opacity: 0, y: 48, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: 48, x: "-50%" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed left-1/2 bottom-5 z-[96] w-[min(42rem,calc(100vw-2rem))]"
        >
          <div className="flex items-center gap-4 px-5 py-3.5 rounded-full bg-[#0E0B08] border border-[#E2B87E]/20 shadow-[0_24px_64px_rgba(0,0,0,0.35),0_10px_40px_rgba(226,184,126,0.2)] text-[#F3EEE3]">
            <button
              type="button"
              data-testid="audio-toggle"
              onClick={toggle}
              aria-label={isPlaying ? `Pause ${current.title}` : `Play ${current.title}`}
              className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br from-[#E8702A] to-[#D43D7E] text-[#0E0B08] shrink-0 hover:brightness-110 hover:scale-105 transition-[filter,transform]"
            >
              {loading ? <Loader2 size={16} className="animate-spin" />
                : isPlaying ? <Pause size={16} fill="currentColor" />
                : <Play size={16} fill="currentColor" />}
            </button>

            <div className="min-w-0 flex-1">
              <div className="text-[17px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis leading-tight">{current.title}</div>
              <div className="text-[17px] text-[#F3EEE3]/75 whitespace-nowrap overflow-hidden text-ellipsis">Gary Malkin</div>
            </div>

            <input
              type="range"
              data-testid="audio-seek-bar"
              className="audio-seek flex-1 min-w-0"
              min={0}
              max={Math.max(duration, 1)}
              step={0.1}
              value={Math.min(progress, duration)}
              onChange={onSeek}
              aria-label="Seek position"
            />

            <div className="flex items-center gap-3.5 shrink-0">
              <span className="audio-time tabular-nums text-[17px] text-[#F3EEE3]/75 min-w-[5.5rem] text-right">
                {fmt(progress)} / {fmt(duration)}
              </span>
              <button
                type="button"
                data-testid="audio-mute"
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Unmute" : "Mute"}
                aria-pressed={muted}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#F3EEE3]/75 hover:text-white hover:bg-white/10 transition-colors"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <input
                type="range"
                data-testid="audio-volume"
                className="audio-volume hidden sm:block w-20"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                aria-label="Volume"
              />
              <button
                type="button"
                data-testid="audio-minimize"
                onClick={() => setMinimized(true)}
                aria-label="Minimize audio player"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#F3EEE3]/75 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ChevronDown size={16} />
              </button>
              <button
                type="button"
                data-testid="audio-close"
                onClick={stop}
                aria-label="Close audio player"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#F3EEE3]/75 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
